import { BitmapText, Container, Sprite, type Texture } from "pixi.js";
import type { PresentationTaskRunner } from "@slot/presentation";
import { jackpotAmountMinor, type JackpotTier } from "../rules/symbols";
import { BITMAP_FONTS, PLAQUE_CAP_HEIGHT } from "../views/textStyles";
import { PLAQUES_LAYOUT } from "./layout";
import { FX_SCOPE } from "./fxScope";

/** The four plaque spots; GRAND's shows ROYAL while GROW is in play (the full board is then 20 cells). */
type PlaqueSpot = "grand" | "major" | "minor" | "mini";
const SPOTS: readonly PlaqueSpot[] = ["grand", "major", "minor", "mini"];

/**
 * Where the bitmap text draws its digits' centre from its anchor, in fractions of its size: the dynamic bitmap font
 * draws each glyph half a stroke right of and above its pen, and centres its line on the font's measured ascent and
 * descent (measured on the game's render against the mock-up).
 */
const DIGITS_FROM_ANCHOR = { x: 0.027, y: 0.0455 };

export interface Plaque {
  root: Container;
  art: Sprite;
  amount: BitmapText;
  /** The width the amount may take (its bar's face), in design units. */
  amountWidth: number;
}

/** A plaque's drawn size for `tier`'s art and its amount (the digits' centre from the plaque's, their height, the widest line), in design units. */
function plaqueGeometry(tier: JackpotTier): { width: number; height: number; amount: { y: number; cap: number; width: number } } {
  const { width, height, amount } = PLAQUES_LAYOUT.art[tier];
  const s = PLAQUES_LAYOUT.scale;
  return { width: width * s, height: height * s, amount: { y: amount.y * s, cap: amount.cap * s, width: amount.width * s } };
}

/** A plaque of `tier`'s art at its drawn size, its amount line on the art's bar (the jackpot plaques', the intro's ROYAL). */
export function plaqueNode(tier: JackpotTier, texture: Texture | null): Plaque {
  const plaque = plaqueGeometry(tier);
  const root = new Container({ label: `Plaque:${tier}` });
  const art = new Sprite({ texture: texture ?? undefined, anchor: 0.5, label: "Art" });
  art.setSize(plaque.width, plaque.height);
  const fontSize = plaque.amount.cap / PLAQUE_CAP_HEIGHT;
  const amount = new BitmapText({ text: "", style: { fontFamily: BITMAP_FONTS.plaque, fontSize }, label: "Amount" });
  amount.anchor.set(0.5);
  amount.position.set(-DIGITS_FROM_ANCHOR.x * fontSize, plaque.amount.y - DIGITS_FROM_ANCHOR.y * fontSize);
  root.addChild(art, amount);
  return { root, art, amount, amountWidth: plaque.amount.width };
}

/**
 * The jackpot plaques: GRAND over MAJOR on the left, MINOR over MINI on the right, each its name over its amount at the
 * current bet (the jackpots are bet multiples: the plaque follows the bet). While GROW is in play the GRAND plaque
 * shows ROYAL. A jackpot won celebrates on its plaque.
 */
export class JackpotPlaquesView extends Container {
  private readonly plaques: Record<PlaqueSpot, Plaque>;
  private readonly textures: Readonly<Record<JackpotTier, Texture | null>>;
  private readonly format: (minor: string) => string;
  private readonly runner: PresentationTaskRunner;
  private betMinor = 0n;
  private royal = false;

  public constructor(input: { textures: Readonly<Record<JackpotTier, Texture | null>>; format: (minor: string) => string; runner: PresentationTaskRunner }) {
    super({ label: "JackpotPlaques" });
    this.textures = input.textures;
    this.format = input.format;
    this.runner = input.runner;
    const build = (spot: PlaqueSpot): Plaque => {
      const plaque = plaqueNode(spot, input.textures[spot] ?? null);
      plaque.root.position.set(PLAQUES_LAYOUT[spot].x, PLAQUES_LAYOUT[spot].y);
      this.addChild(plaque.root);
      return plaque;
    };
    this.plaques = { grand: build("grand"), major: build("major"), minor: build("minor"), mini: build("mini") };
  }

  /** Every amount at `betMinor`. */
  public setBet(betMinor: bigint): void {
    this.betMinor = betMinor;
    for (const spot of SPOTS) this.refresh(spot);
  }

  /** GRAND's plaque shows ROYAL (GROW in play) or GRAND. */
  public setRoyal(royal: boolean): void {
    this.royal = royal;
    const tier: JackpotTier = royal ? "royal" : "grand";
    const art = this.textures[tier];
    if (art) {
      const plaque = plaqueGeometry(tier);
      this.plaques.grand.art.texture = art;
      this.plaques.grand.art.setSize(plaque.width, plaque.height);
    }
    this.refresh("grand");
  }

  /** A plaque's node, its art and amount (the shine glints across it); ROYAL's is GRAND's spot. */
  public node(tier: JackpotTier): Container {
    return this.plaques[tier === "royal" ? "grand" : tier].root;
  }

  /** A plaque's bar's gold rim in the composition's space (where its sparkles twinkle). */
  public rim(tier: JackpotTier): { x: number; y: number; width: number; height: number } {
    const { root } = this.plaques[tier === "royal" ? "grand" : tier];
    const { width, height, rim } = PLAQUES_LAYOUT.art[tier];
    const s = PLAQUES_LAYOUT.scale;
    return { x: root.x + (rim[0] - width / 2) * s, y: root.y + (rim[1] - height / 2) * s, width: (rim[2] - rim[0]) * s, height: (rim[3] - rim[1]) * s };
  }

  /** A jackpot won: its plaque swells twice. */
  public async celebrate(tier: JackpotTier, seconds: number): Promise<void> {
    const root = this.plaques[tier === "royal" ? "grand" : tier].root;
    await this.runner.tweenProgress(seconds, (k) => {
      if (root.destroyed) return;
      const s = 1 + 0.16 * Math.abs(Math.sin(2 * Math.PI * k));
      root.scale.set(s, s);
    }, FX_SCOPE, { easing: "linear" });
    root.scale.set(1, 1);
  }

  private refresh(spot: PlaqueSpot): void {
    const tier: JackpotTier = spot === "grand" && this.royal ? "royal" : spot;
    const { amount, amountWidth } = this.plaques[spot];
    amount.text = this.format(jackpotAmountMinor(tier, this.betMinor).toString());
    const natural = amount.width / (amount.scale.x || 1);
    const scale = Math.min(1, amountWidth / Math.max(1, natural));
    amount.scale.set(scale, scale);
  }
}
